import React, { useEffect, useState } from 'react';
import { ProductSelector } from '../ProductSelector';
import { WeldingConfigGroups } from '../modules/WeldingConfigDrawer';
import { ProposalFormData } from '../../types';
import { isModuleKey, moduleTotalsFromBreakdown, ModuleState } from '../modules/moduleDefs';
import { Button } from '../../../../shared/components/Button';
import { proposalService } from '../../services/proposalService';
import { configurationService } from '../../../configuration/services/configurationService';
import { buildPricingSheetData, buildInternalSplitData, buildModuleCostRows, ModuleBreakdownDetails, PRODUCT_DISPLAY_NAMES } from '../../utils/pricingSheetData';
import { downloadPricingSheetPDF } from '../PricingSheetPDF';
import { downloadPricingSheetExcel } from '../../utils/pricingSheetExcel';
import { downloadInternalSplitPDF } from '../InternalSplitPDF';

interface PricingSectionProps {
  formData: ProposalFormData;
  onFormDataChange: React.Dispatch<React.SetStateAction<ProposalFormData>>;
  onPriceCalculated?: (total: number, breakdown: unknown, refNumber: string) => void;
  // Last saved pricing, so an opened proposal shows its numbers without a
  // recalculation. Seed local state once on mount.
  initialTotal?: number;
  initialBreakdown?: unknown;
  initialRefNumber?: string;
}

export const PricingSection: React.FC<PricingSectionProps> = ({
  formData,
  onFormDataChange,
  onPriceCalculated,
  initialTotal,
  initialBreakdown,
  initialRefNumber
}) => {
  const [totalPrice, setTotalPrice] = useState<number>(initialTotal ?? 0);
  const [priceBreakdown, setPriceBreakdown] = useState<{
    products?: Record<string, {
      total?: number;
      details?: {
        cost?: number;
        durationMonths?: number;
        totalQuantity?: number;
        actualUnits?: number;
        spareUnits?: number;
        softwareChargeableUnits?: number;
        otherCostChargeableUnits?: number;
        hardwareCost?: number;
        softwareCost?: number;
        mobilizationCost?: number;
        basePrice?: number;
        discountPercentage?: number;
        modeMix?: {
          liveUnits?: number;
          offlineOnlyUnits?: number;
          weightedConnectedUnits?: number;
          liveModeMultiplier?: number;
          offlineModeMultiplier?: number;
        };
        otherCosts?: {
          internet?: number;
          cloud?: number;
          remoteSupport?: number;
          training?: number;
          provisioning?: number;
          onFieldSupport?: number;
        };
        spares?: Record<string, {
          actualUnits?: number;
          spareUnits?: number;
          sparePricingMode?: 'hw_only' | 'hw_sw' | 'hw_sw_other';
          softwareChargeableUnits?: number;
          otherCostChargeableUnits?: number;
        }>;
        totalOtherCosts?: number;
        totalPrice?: number;
        // Per-module one-time / recurring split lines, used by the inline
        // breakdown panel and the internal split. Present for DATA 360 modules.
        oneTime?: ModuleBreakdownDetails['oneTime'];
        recurring?: ModuleBreakdownDetails['recurring'];
      };
    }>;
    // Project-level platform + retention, added once across all modules. Not
    // attributed to any single module, so it explains the gap between the rail
    // module totals and the grand total.
    projectLevel?: { total?: number } | null;
    total?: number;
  } | null>((initialBreakdown as never) ?? null);
  const [isBreakdownModalOpen, setIsBreakdownModalOpen] = useState(false);
  const [isCalculating, setIsCalculating] = useState(false);
  const [isDownloadingPDF, setIsDownloadingPDF] = useState(false);
  const [isDownloadingInternalPDF, setIsDownloadingInternalPDF] = useState(false);
  const [customRefNumber, setCustomRefNumber] = useState(initialRefNumber ?? '');
  const [moduleConfigDefaults, setModuleConfigDefaults] = useState<{
    material?: Record<string, number>;
    nre?: Record<string, number>;
    platform?: Record<string, number>;
    retention?: Record<string, number>;
    durationDiscount?: Record<string, number>;
    pipefitter?: Record<string, number>;
    ndt?: Record<string, number>;
    coating?: Record<string, number>;
  }>({});
  const [weldingConfig, setWeldingConfig] = useState<WeldingConfigGroups>({});
  const [rateDefaults, setRateDefaults] = useState<{
    remoteSupportHourlyRate?: number;
    trainingHourlyRate?: number;
    trainingQuickHours?: number;
    trainingMediumHours?: number;
    trainingLongHours?: number;
    provisioningHourlyRate?: number;
    onFieldSupportDailyRate?: number;
    mobilizingShippingCostPerKg?: number;
    defaultSparePricingMode?: 'hw_only' | 'hw_sw' | 'hw_sw_other';
    allowSparePricingModeOverride?: boolean;
  }>({});

  useEffect(() => {
    const loadRateDefaults = async () => {
      try {
        const config = await configurationService.getCurrentConfiguration();
        setModuleConfigDefaults({
          material: config.material_config as unknown as Record<string, number> | undefined,
          nre: config.nre_config as unknown as Record<string, number> | undefined,
          platform: config.platform_config as unknown as Record<string, number> | undefined,
          retention: config.retention_config as unknown as Record<string, number> | undefined,
          durationDiscount: config.duration_discount_config as unknown as Record<string, number> | undefined,
          pipefitter: config.pipefitter_config as unknown as Record<string, number> | undefined,
          ndt: config.ndt_config as unknown as Record<string, number> | undefined,
          coating: config.coating_config as unknown as Record<string, number> | undefined,
        });
        setRateDefaults({
          remoteSupportHourlyRate: config.other_costs_config.maintenanceAndSupport ?? 0,
          trainingHourlyRate: config.other_costs_config.trainingRate ?? 0,
          trainingQuickHours: config.other_costs_config.trainingQuickHours ?? 1,
          trainingMediumHours: config.other_costs_config.trainingMediumHours ?? 4,
          trainingLongHours: config.other_costs_config.trainingLongHours ?? 8,
          provisioningHourlyRate: config.other_costs_config.provisioningHourlyRate ?? 0,
          onFieldSupportDailyRate: config.other_costs_config.onFieldSupportDailyRate ?? 0,
          mobilizingShippingCostPerKg: config.other_costs_config.shippingCostPerKg ?? 0,
          defaultSparePricingMode: config.other_costs_config.defaultSparePricingMode ?? 'hw_only',
          allowSparePricingModeOverride: config.other_costs_config.allowSparePricingModeOverride ?? true
        });
        setWeldingConfig({
          hardware: config.hardware_config as unknown as Record<string, number>,
          application: config.application_config as unknown as Record<string, number>,
          other_costs: config.other_costs_config as unknown as Record<string, number>,
          discount: config.duration_discount_config as unknown as Record<string, number>,
        });
      } catch (error) {
        console.error('Failed to load pricing defaults:', error);
      }
    };

    loadRateDefaults();
  }, []);
  const weldingProductsRequiringMode = new Set(['m500', 'p600z', 'p625', 'iwm', 'manual']);
  const weldingItemsRequiringQuantity = new Set([
    'm500',
    'p600z',
    'p625',
    'iwm',
    'manual',
    'remoteSupport',
    'provisioning',
    'onFieldSupport',
    'training'
  ]);

  const validateTimeline = () => {
    if (!formData.startDate || !formData.endDate) return false;
    const start = new Date(formData.startDate);
    const end = new Date(formData.endDate);
    if (isNaN(start.getTime()) || isNaN(end.getTime())) return false;
    return start < end;
  };

  const getProductValidationState = () => {
    const products = formData.selectedProducts;
    let hasValidSelection = false;
    let hasInvalidWeldingSelection = false;
    let hasModuleMissingVolume = false;

    Object.entries(products).forEach(([productId, product]) => {
      const items = product.items || {};

      if (isModuleKey(productId)) {
        if (product.isSelected) {
          if (typeof product.volume === 'number' && product.volume > 0) {
            hasValidSelection = true;
          } else {
            hasModuleMissingVolume = true;
          }
        }
        return;
      }

      if (productId === 'welding') {
        Object.entries(items).forEach(([itemId, item]) => {
          if (itemId.includes('_live') || itemId.includes('_offline') || !item.selected) return;

          const quantity = item.quantity || 0;
          const spareQuantity = item.spareQuantity || 0;
          const needsQuantity = weldingItemsRequiringQuantity.has(itemId);
          const hasRequiredQuantity = !needsQuantity || (quantity + spareQuantity) > 0;

          const needsMode = weldingProductsRequiringMode.has(itemId);
          const hasRequiredMode = !needsMode || Boolean(
            items[`${itemId}_live`]?.selected || items[`${itemId}_offline`]?.selected
          );

          if (hasRequiredQuantity && hasRequiredMode) {
            hasValidSelection = true;
          } else {
            hasInvalidWeldingSelection = true;
          }
        });
        return;
      }

      Object.values(items).forEach(item => {
        if (item.selected) hasValidSelection = true;
      });
    });

    return { hasValidSelection, hasInvalidWeldingSelection, hasModuleMissingVolume };
  };

  const validateProducts = () => {
    const { hasValidSelection, hasInvalidWeldingSelection, hasModuleMissingVolume } =
      getProductValidationState();
    return hasValidSelection && !hasInvalidWeldingSelection && !hasModuleMissingVolume;
  };

  const isCalculateEnabled = () => validateTimeline() && validateProducts() && !isCalculating;

  const getValidationMessages = () => {
    const messages: string[] = [];
    if (!validateTimeline()) messages.push('Please set a valid start and end date');
    const { hasValidSelection, hasInvalidWeldingSelection, hasModuleMissingVolume } =
      getProductValidationState();
    if (hasModuleMissingVolume) {
      messages.push('Enter a volume for each selected module');
    }
    if (hasInvalidWeldingSelection) {
      messages.push('Please complete all selected welding items');
    } else if (!hasValidSelection) {
      messages.push('Please select at least one product');
    }
    return messages;
  };

  const handleCalculatePrice = async () => {
    if (!isCalculateEnabled()) return;
    setIsCalculating(true);
    try {
      const response = await proposalService.calculatePrice({
        selectedProducts: formData.selectedProducts,
        includePlatform: formData.includePlatform ?? true,
        numberOfUsers: formData.numberOfUsers ?? 0,
        retentionTier: formData.retentionTier ?? null,
        retentionYears: formData.retentionYears ?? 0,
        customerManagedCloud: formData.customerManagedCloud ?? false,
        platformOverrides: formData.platformOverrides ?? {},
        retentionOverrides: formData.retentionOverrides ?? {},
        timeline: {
          startDate: formData.startDate || '',
          endDate: formData.endDate || '',
          duration: {
            value: formData.duration?.value || '',
            unit: formData.duration?.unit || 'months'
          },
          milestones: formData.milestones || []
        }
      });
      const calculatedTotal = response.totalPrice ?? 0;
      setTotalPrice(calculatedTotal);
      setPriceBreakdown(response.breakdown ?? null);
      onFormDataChange(prev => ({ ...prev, totalValue: String(calculatedTotal) }));
      onPriceCalculated?.(calculatedTotal, response.breakdown ?? null, customRefNumber);
    } catch (error) {
      console.error('Failed to calculate price:', error);
    } finally {
      setIsCalculating(false);
    }
  };

  const handleDownloadPDF = async () => {
    if (!priceBreakdown || totalPrice <= 0) return;
    setIsDownloadingPDF(true);
    try {
      const sheetData = buildPricingSheetData(formData, priceBreakdown, totalPrice, customRefNumber || undefined);
      await downloadPricingSheetPDF(sheetData);
    } catch (err) {
      console.error('PDF generation failed:', err);
    } finally {
      setIsDownloadingPDF(false);
    }
  };

  const handleDownloadInternalPDF = async () => {
    if (!priceBreakdown || totalPrice <= 0) return;
    setIsDownloadingInternalPDF(true);
    try {
      const splitData = buildInternalSplitData(formData, priceBreakdown, totalPrice, customRefNumber || undefined);
      await downloadInternalSplitPDF(splitData);
    } catch (err) {
      console.error('Internal PDF generation failed:', err);
    } finally {
      setIsDownloadingInternalPDF(false);
    }
  };

  const handleDownloadExcel = () => {
    if (!priceBreakdown || totalPrice <= 0) return;
    try {
      const sheetData = buildPricingSheetData(formData, priceBreakdown, totalPrice, customRefNumber || undefined);
      downloadPricingSheetExcel(sheetData);
    } catch (err) {
      console.error('Excel generation failed:', err);
    }
  };

  const moduleTotals = moduleTotalsFromBreakdown(priceBreakdown);

  // Internal cost-vs-price split shown in the View Split modal, so a rep can see
  // module cost and margin live without downloading the internal PDF.
  const costRows = buildModuleCostRows(priceBreakdown?.products);
  const totalModuleCost = costRows.reduce((sum, row) => sum + row.cost, 0);
  const totalModulePrice = costRows.reduce((sum, row) => sum + row.price, 0);
  const blendedCostMargin =
    totalModulePrice > 0 ? ((totalModulePrice - totalModuleCost) / totalModulePrice) * 100 : 0;

  // Project months and the duration-band discount, mirroring the backend, so the
  // Platform Subscription pane can show the period total live as rates change.
  const projectMonths = (() => {
    const value = Number(formData.duration?.value) || 0;
    const unit = formData.duration?.unit;
    if (unit === 'years') return value * 12;
    if (unit === 'days') return value / 30;
    return value;
  })();
  const platformDiscountPct = (() => {
    const bands = moduleConfigDefaults.durationDiscount;
    if (!bands) return 0;
    if (projectMonths <= 12) return bands.lessThan12Month ?? 0;
    if (projectMonths <= 24) return bands.month13To24 ?? 0;
    if (projectMonths <= 36) return bands.month25To36 ?? 0;
    if (projectMonths <= 60) return bands.month37To60 ?? 0;
    return bands.perpetual ?? 0;
  })();

  const handleModuleChange = (productId: string, patch: Partial<ModuleState>) => {
    onFormDataChange(prev => {
      const current = prev.selectedProducts[productId] || { isSelected: true, items: {} };
      return {
        ...prev,
        selectedProducts: {
          ...prev.selectedProducts,
          [productId]: { ...current, ...patch },
        },
      };
    });
  };

  const handleServiceSelection = (serviceId: string, isSelected: boolean) => {
    onFormDataChange(prev => ({
      ...prev,
      selectedProducts: {
        ...prev.selectedProducts,
        [serviceId]: { isSelected, items: prev.selectedProducts[serviceId]?.items || {} }
      }
    }));
  };

  const formatCurrency = (value?: number) => {
    const amount = Number.isFinite(value) ? value as number : 0;
    return `$${amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const weldingDetails = priceBreakdown?.products?.welding?.details;

  return (
    <div className="space-y-6">
      <div className="bg-white shadow rounded-xl p-6">
        {/* Header: title left, button + total right — inline */}
        <div className="flex justify-between items-start mb-6">
          <div>
            <h2 className="text-xl font-semibold font-heading text-blueprint-blue">Pricing</h2>
            <p className="mt-1 text-sm text-heritage-grey">Select the products and services for this proposal</p>
          </div>
          <div className="flex flex-col items-end gap-2 ml-6 min-w-0">
            {getValidationMessages().map((msg, i) => (
              <p key={i} className="text-sm text-readable-red">{msg}</p>
            ))}
            {/* Row 1: total (if calculated) + calculate button */}
            <div className="flex items-center gap-4">
              {totalPrice > 0 && (
                <div className="flex flex-col items-end leading-tight">
                  <span className="text-xl font-bold text-blueprint-blue">
                    Total: ${totalPrice.toLocaleString()}
                  </span>
                  {(priceBreakdown?.projectLevel?.total ?? 0) > 0 && (
                    <span className="text-xs font-normal text-heritage-grey">
                      Modules {formatCurrency(totalPrice - (priceBreakdown?.projectLevel?.total ?? 0))}
                      {' + '}
                      Platform &amp; subscription {formatCurrency(priceBreakdown?.projectLevel?.total ?? 0)}
                    </span>
                  )}
                </div>
              )}
              <Button
                variant="primary"
                onClick={handleCalculatePrice}
                disabled={!isCalculateEnabled()}
                className="px-4 py-2 bg-[#EB1C37] text-white rounded-lg hover:bg-[#EB1C37] disabled:opacity-50 whitespace-nowrap"
              >
                {isCalculating ? 'Calculating...' : 'Calculate Price'}
              </Button>
            </div>
            {/* Row 2: Ref # + action buttons — only shown after calculation */}
            {totalPrice > 0 && priceBreakdown && (
              <div className="flex items-center gap-3 flex-wrap justify-end">
                <div className="flex items-center gap-1.5">
                  <label className="text-xs text-heritage-grey whitespace-nowrap">Ref #</label>
                  <input
                    type="text"
                    value={customRefNumber}
                    onChange={e => {
                      setCustomRefNumber(e.target.value);
                      if (totalPrice > 0) onPriceCalculated?.(totalPrice, priceBreakdown, e.target.value);
                    }}
                    placeholder="Auto-generated"
                    className="text-xs border border-[#E2E5EA] rounded px-2 py-1 w-36 focus:outline-none focus:ring-1 focus:ring-hero-red"
                  />
                </div>
                <div className="w-px h-4 bg-[#E2E5EA] self-center" />
                <button
                  type="button"
                  onClick={() => setIsBreakdownModalOpen(true)}
                  className="text-sm text-blueprint-blue hover:text-hero-red font-medium whitespace-nowrap"
                >
                  View Split &amp; Cost
                </button>
                <button
                  type="button"
                  onClick={handleDownloadPDF}
                  disabled={isDownloadingPDF}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-hero-red rounded-lg disabled:opacity-50 disabled:cursor-wait transition-opacity whitespace-nowrap"
                >
                  <svg className="h-4 w-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                      d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  {isDownloadingPDF ? 'Generating...' : 'Download PDF'}
                </button>
                <button
                  type="button"
                  onClick={handleDownloadExcel}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-galvanizing-green hover:bg-[#187A2E] rounded-lg transition-colors whitespace-nowrap"
                >
                  <svg className="h-4 w-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                      d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Download Excel
                </button>
              </div>
            )}
          </div>
        </div>

        <ProductSelector
          selectedProducts={formData.selectedProducts}
          moduleTotals={moduleTotals}
          productBreakdowns={priceBreakdown?.products}
          moduleConfigDefaults={moduleConfigDefaults}
          pipefitterConfig={moduleConfigDefaults.pipefitter}
          ndtConfig={moduleConfigDefaults.ndt}
          coatingConfig={moduleConfigDefaults.coating}
          weldingConfig={weldingConfig}
          weldingBreakdown={weldingDetails}
          weldingOverrides={formData.selectedProducts.welding?.overrides}
          onWeldingOverrideChange={(key, value) =>
            onFormDataChange(prev => {
              const product = prev.selectedProducts.welding ?? { isSelected: false, items: {} };
              const next = { ...(product.overrides ?? {}) };
              if (value === undefined || Number.isNaN(value)) delete next[key];
              else next[key] = value;
              return {
                ...prev,
                selectedProducts: {
                  ...prev.selectedProducts,
                  welding: { ...product, overrides: next },
                },
              };
            })
          }
          onWeldingResetAll={() =>
            onFormDataChange(prev => {
              const product = prev.selectedProducts.welding;
              if (!product) return prev;
              return {
                ...prev,
                selectedProducts: {
                  ...prev.selectedProducts,
                  welding: { ...product, overrides: {} },
                },
              };
            })
          }
          includePlatform={formData.includePlatform ?? true}
          onIncludePlatformChange={(included) =>
            onFormDataChange(prev => ({ ...prev, includePlatform: included }))
          }
          platformOptions={{
            numberOfUsers: formData.numberOfUsers ?? 0,
            retentionTier: formData.retentionTier ?? null,
            retentionYears: formData.retentionYears ?? 0,
            customerManagedCloud: formData.customerManagedCloud ?? false,
          }}
          onPlatformOptionsChange={(patch) =>
            onFormDataChange(prev => ({ ...prev, ...patch }))
          }
          platformTotal={priceBreakdown?.projectLevel?.total}
          platformConfig={moduleConfigDefaults.platform}
          retentionConfig={moduleConfigDefaults.retention}
          platformMonths={projectMonths}
          platformDiscountPct={platformDiscountPct}
          platformOverrides={formData.platformOverrides}
          onPlatformOverrideChange={(key, value) =>
            onFormDataChange(prev => {
              const next = { ...(prev.platformOverrides ?? {}) };
              if (value === undefined || Number.isNaN(value)) delete next[key];
              else next[key] = value;
              return { ...prev, platformOverrides: next };
            })
          }
          retentionOverrides={formData.retentionOverrides}
          onRetentionOverrideChange={(key, value) =>
            onFormDataChange(prev => {
              const next = { ...(prev.retentionOverrides ?? {}) };
              if (value === undefined || Number.isNaN(value)) delete next[key];
              else next[key] = value;
              return { ...prev, retentionOverrides: next };
            })
          }
          onModuleChange={handleModuleChange}
          onProductChange={(productId, isSelected) => handleServiceSelection(productId, isSelected)}
          onProductItemChange={(productId, itemId, isSelected) => {
            onFormDataChange(prev => {
              const currentProduct = prev.selectedProducts[productId] || { isSelected: false, items: {} };
              const nextItems = {
                ...currentProduct.items,
                [itemId]: {
                  ...currentProduct.items[itemId],
                  selected: isSelected
                }
              };

              const hasSelectedBaseItem = Object.entries(nextItems).some(([key, item]) => {
                const isModeFlag = key.endsWith('_live') || key.endsWith('_offline');
                return !isModeFlag && item.selected;
              });

              return {
                ...prev,
                selectedProducts: {
                  ...prev.selectedProducts,
                  [productId]: {
                    ...currentProduct,
                    isSelected: hasSelectedBaseItem,
                    items: nextItems
                  }
                }
              };
            });
          }}
          onQuantityChange={(productId, itemId, quantity) => {
            onFormDataChange(prev => ({
              ...prev,
              selectedProducts: {
                ...prev.selectedProducts,
                [productId]: {
                  ...prev.selectedProducts[productId],
                  items: {
                    ...prev.selectedProducts[productId]?.items,
                    [itemId]: {
                      ...prev.selectedProducts[productId]?.items[itemId],
                      quantity
                    }
                  }
                }
              }
            }));
          }}
          onSpareQuantityChange={(productId, itemId, spareQuantity) => {
            onFormDataChange(prev => ({
              ...prev,
              selectedProducts: {
                ...prev.selectedProducts,
                [productId]: {
                  ...prev.selectedProducts[productId],
                  items: {
                    ...prev.selectedProducts[productId]?.items,
                    [itemId]: {
                      ...prev.selectedProducts[productId]?.items[itemId],
                      spareQuantity
                    }
                  }
                }
              }
            }));
          }}
          onSparePricingModeChange={(productId, itemId, sparePricingMode) => {
            onFormDataChange(prev => ({
              ...prev,
              selectedProducts: {
                ...prev.selectedProducts,
                [productId]: {
                  ...prev.selectedProducts[productId],
                  items: {
                    ...prev.selectedProducts[productId]?.items,
                    [itemId]: {
                      ...prev.selectedProducts[productId]?.items[itemId],
                      sparePricingMode
                    }
                  }
                }
              }
            }));
          }}
          onUnitRateChange={(productId, itemId, unitRate) => {
            onFormDataChange(prev => ({
              ...prev,
              selectedProducts: {
                ...prev.selectedProducts,
                [productId]: {
                  ...prev.selectedProducts[productId],
                  items: {
                    ...prev.selectedProducts[productId]?.items,
                    [itemId]: {
                      ...prev.selectedProducts[productId]?.items[itemId],
                      unitRate
                    }
                  }
                }
              }
            }));
          }}
          onTrainingDurationTypeChange={(productId, itemId, trainingDurationType) => {
            onFormDataChange(prev => ({
              ...prev,
              selectedProducts: {
                ...prev.selectedProducts,
                [productId]: {
                  ...prev.selectedProducts[productId],
                  items: {
                    ...prev.selectedProducts[productId]?.items,
                    [itemId]: {
                      ...prev.selectedProducts[productId]?.items[itemId],
                      trainingDurationType
                    }
                  }
                }
              }
            }));
          }}
          serviceRateDefaults={rateDefaults}
          formData={formData}
        />
      </div>

      {isBreakdownModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-3xl rounded-xl bg-white shadow-xl">
            <div className="flex items-center justify-between border-b px-5 py-4">
              <h3 className="text-lg font-semibold font-heading text-blueprint-blue">Price &amp; Cost Split</h3>
              <button
                type="button"
                onClick={() => setIsBreakdownModalOpen(false)}
                className="text-heritage-grey hover:text-blueprint-blue"
              >
                Close
              </button>
            </div>

            <div className="max-h-[70vh] overflow-y-auto px-5 py-4 space-y-6">
              <div>
                <p className="text-sm font-medium text-text-grey mb-2">Product Totals</p>
                <div className="space-y-1 text-sm text-text-grey">
                  {Object.entries(priceBreakdown?.products || {})
                    .filter(([, product]) => (product.total ?? 0) > 0)
                    .map(([productId, product]) => (
                      <div key={productId} className="flex justify-between">
                        <span>{PRODUCT_DISPLAY_NAMES[productId] ?? productId}</span>
                        <span className="font-semibold">{formatCurrency(product.total)}</span>
                      </div>
                    ))}
                  {(priceBreakdown?.projectLevel?.total ?? 0) > 0 && (
                    <div className="flex justify-between">
                      <span>Platform &amp; Subscription</span>
                      <span className="font-semibold">
                        {formatCurrency(priceBreakdown?.projectLevel?.total)}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Internal cost-vs-price split, one block per module */}
              <div>
                <p className="text-sm font-medium text-text-grey mb-2">Module Cost Summary (Internal)</p>
                {costRows.length === 0 ? (
                  <p className="text-xs text-heritage-grey">
                    No cost data. Recalculate the price (requires the latest backend) to see the cost split.
                  </p>
                ) : (
                  <div className="space-y-3">
                    {costRows.map((row, i) => (
                      <div key={i} className="rounded border border-[#E2E5EA] p-3">
                        <div className="flex justify-between font-medium text-blueprint-blue">
                          <span>{row.label}</span>
                          <span>{formatCurrency(row.cost)}</span>
                        </div>
                        {row.lines.length > 0 && (
                          <div className="mt-1 space-y-0.5">
                            {row.lines.map((line, j) => (
                              <div key={j} className="flex justify-between gap-3 text-xs text-text-grey">
                                <span>
                                  {line.label}
                                  {line.note ? (
                                    <span className="text-heritage-grey"> · {line.note}</span>
                                  ) : null}
                                </span>
                                <span className="whitespace-nowrap">{formatCurrency(line.cost)}</span>
                              </div>
                            ))}
                          </div>
                        )}
                        <div className="mt-2 flex justify-between border-t pt-1 text-xs text-heritage-grey">
                          <span>Price {formatCurrency(row.price)}</span>
                          <span>Margin {row.marginPct.toFixed(1)}%</span>
                        </div>
                      </div>
                    ))}
                    <div className="flex justify-between border-t pt-2 font-semibold text-blueprint-blue">
                      <span>All Modules Cost</span>
                      <span>{formatCurrency(totalModuleCost)}</span>
                    </div>
                    <div className="flex justify-between text-xs text-heritage-grey">
                      <span>Price {formatCurrency(totalModulePrice)}</span>
                      <span>Margin {blendedCostMargin.toFixed(1)}%</span>
                    </div>
                    <p className="text-[11px] text-heritage-grey">
                      Cost is pre-margin internal cost (COGS). Lines without a modelled cost are shown
                      at zero margin, so the margin is conservative. Platform &amp; subscription is excluded.
                    </p>
                  </div>
                )}
              </div>

              {weldingDetails && (
                <div>
                  <p className="text-sm font-medium text-text-grey mb-2">Welding Detailed Split</p>
                  <div className="space-y-1 text-sm text-text-grey">
                    <div className="flex justify-between"><span>Hardware</span><span>{formatCurrency(weldingDetails.hardwareCost)}</span></div>
                    <div className="flex justify-between"><span>DATA 360 Platform License</span><span>{formatCurrency(weldingDetails.softwareCost)}</span></div>
                    <div className="flex justify-between"><span>Mobilization</span><span>{formatCurrency(weldingDetails.mobilizationCost)}</span></div>
                    <div className="flex justify-between font-semibold"><span>Base Price</span><span>{formatCurrency(weldingDetails.basePrice)}</span></div>
                    <div className="border-t my-2" />
                    <div className="flex justify-between"><span>Internet</span><span>{formatCurrency(weldingDetails.otherCosts?.internet)}</span></div>
                    <div className="flex justify-between"><span>Cloud</span><span>{formatCurrency(weldingDetails.otherCosts?.cloud)}</span></div>
                    <div className="flex justify-between"><span>Remote Support</span><span>{formatCurrency(weldingDetails.otherCosts?.remoteSupport)}</span></div>
                    <div className="flex justify-between"><span>Training</span><span>{formatCurrency(weldingDetails.otherCosts?.training)}</span></div>
                    <div className="flex justify-between"><span>Provisioning</span><span>{formatCurrency(weldingDetails.otherCosts?.provisioning)}</span></div>
                    <div className="flex justify-between"><span>On-field Support</span><span>{formatCurrency(weldingDetails.otherCosts?.onFieldSupport)}</span></div>
                    <div className="flex justify-between font-semibold"><span>Total Other Costs</span><span>{formatCurrency(weldingDetails.totalOtherCosts)}</span></div>
                    <div className="border-t my-2" />
                    <div className="flex justify-between font-semibold"><span>Welding Total</span><span>{formatCurrency(weldingDetails.totalPrice)}</span></div>
                  </div>
                  <div className="mt-3 rounded bg-page-bg p-3 text-xs text-text-grey">
                    <p>Duration: {weldingDetails.durationMonths ?? 0} months</p>
                    <p>Total units: {weldingDetails.totalQuantity ?? 0}</p>
                    <p>Actual units: {weldingDetails.actualUnits ?? 0}</p>
                    <p>Spare units: {weldingDetails.spareUnits ?? 0}</p>
                    <p>Software chargeable units: {weldingDetails.softwareChargeableUnits ?? 0}</p>
                    <p>Other-cost chargeable units: {weldingDetails.otherCostChargeableUnits ?? 0}</p>
                    <p>
                      Internet/Cloud mode mix: live units {weldingDetails.modeMix?.liveUnits ?? 0},
                      offline-only units {weldingDetails.modeMix?.offlineOnlyUnits ?? 0},
                      weighted units {Number(weldingDetails.modeMix?.weightedConnectedUnits ?? 0).toFixed(2)}
                    </p>
                    <p>
                      Multipliers used: live x{weldingDetails.modeMix?.liveModeMultiplier ?? 1},
                      offline x{weldingDetails.modeMix?.offlineModeMultiplier ?? 0.5}
                    </p>
                    {weldingDetails.spares && Object.keys(weldingDetails.spares).length > 0 && (
                      <div className="mt-2">
                        <p className="font-medium">Per-system spare charging:</p>
                        {Object.entries(weldingDetails.spares).map(([systemId, spareDetails]) => (
                          <p key={systemId}>
                            {systemId}: actual {spareDetails.actualUnits ?? 0}, spares {spareDetails.spareUnits ?? 0},
                            mode {spareDetails.sparePricingMode ?? 'hw_only'}
                          </p>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="flex items-center justify-between border-t px-5 py-4">
              <button
                type="button"
                onClick={handleDownloadInternalPDF}
                disabled={isDownloadingInternalPDF}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-hero-red rounded-lg disabled:opacity-50 disabled:cursor-wait transition-opacity"
              >
                <svg className="h-4 w-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                {isDownloadingInternalPDF ? 'Generating...' : 'Download Internal PDF'}
              </button>
              <Button
                type="button"
                variant="secondary"
                onClick={() => setIsBreakdownModalOpen(false)}
                className="px-4 py-2 border border-[#E2E5EA] rounded-lg hover:bg-page-bg"
              >
                Close
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
